// LinkAvatar — the visual mark for "Link", Nexilink's AI onboarding
// assistant.
//
// Design: two interlocking rings (a chain link), white-stroked on a
// Nexilink-teal circle. The chain-link mark is a direct nod to the
// name "Link" AND to Nexilink itself (which links employers and
// employees). One ring goes top-left, the other bottom-right, with
// a small gap in the back ring's stroke where the front ring passes
// over — gives the proper 3D interlocked look at any size.
//
// Sizing comes from the `className` prop (e.g. "w-5 h-5", "w-9 h-9").
// Colors override-able via bg/fg props for dark-mode / inverse uses.
//
// This is still placeholder-tier work that I (Claude) wrote — for a
// production launch a proper design pass from someone who does this
// professionally is recommended.

import React from 'react';

export interface LinkAvatarProps {
    className?: string;
    /** Override the fill of the circle. Defaults to Nexilink teal. */
    bg?: string;
    /** Override the stroke of the chain rings. Defaults to white. */
    fg?: string;
    title?: string;
}

export const LinkAvatar: React.FC<LinkAvatarProps> = ({
    className,
    bg = '#1A4D4F',
    fg = '#FFFFFF',
    title = 'Link — Nexilinks AI-assistent',
}) => {
    return (
        <svg
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            role="img"
            aria-label={title}
        >
            <title>{title}</title>
            <circle cx="16" cy="16" r="16" fill={bg} />
            {/* Back ring (top-left), drawn first. Centered at (13,13),
                radius 5.5. Path is the SVG "full circle as one arc"
                idiom: move to top-of-ring, sweep almost all the way
                round. */}
            <circle
                cx="13"
                cy="13"
                r="5.5"
                stroke={fg}
                strokeWidth="2.4"
                fill="none"
            />
            {/* Teal cap that masks the back ring's lower-right arc
                where the front ring would interlock — gives the
                back stroke a "passes behind" cut so the two rings
                visually link instead of just overlapping. */}
            <circle cx="16" cy="16" r="2.5" fill={bg} />
            {/* Front ring (bottom-right), drawn last so it sits on
                top of the cap. Centered at (19,19), radius 5.5;
                with the back ring 8.5 px away (centers diagonal),
                they overlap by ~2.5 px on each side — a real chain-
                link interlock. */}
            <circle
                cx="19"
                cy="19"
                r="5.5"
                stroke={fg}
                strokeWidth="2.4"
                fill="none"
            />
        </svg>
    );
};
